import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  InputBase,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CHAT_FILTERS, CV2_COLUMN_HEADER_HEIGHT } from "../../../constants/chatV2Constants";
import { filterCount } from "../../../utils/chatV2Helpers";
import { IconChat24 } from "./ChatV2Icons";
import { ReactComponent as Search } from "../../../assets/images/TicketImages/search.svg";

const headerRowSx = {
  height: CV2_COLUMN_HEADER_HEIGHT,
  minHeight: CV2_COLUMN_HEADER_HEIGHT,
  boxSizing: "border-box",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
};

const SUB_LIST_RAIL_GUTTER_PX = 20;
const ACTIVE_RAIL_LEFT_PX = -10;

function ChatNavSidebar({
  chatCounts,
  activeFilterId = "all",
  onFilterSelect,
  searchQuery = "",
  onSearchChange,
}) {
  const [chatOpen, setChatOpen] = useState(true);

  return (
    <Box
      component="aside"
      sx={{
        width: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box sx={headerRowSx}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#061522", lineHeight: "24px" }}>
          Livechat
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2, pt: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 32,
            color: "#061522",
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            padding: "0 12px",
            boxShadow: "0px 1px 2px 0px rgba(6,21,34,0.05)",
          }}
        >
          <Search />
          <InputBase
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search"
            inputProps={{ "aria-label": "Search chats" }}
            sx={{
              flex: 1,
              fontSize: 14,
              "& input::placeholder": { color: "#A5B2BD", opacity: 1 },
            }}
          />
        </Box>

        <Box>
          <ListItemButton
            onClick={() => setChatOpen((v) => !v)}
            sx={{
              borderRadius: "9px",
              px: 1,
              pl:0,
              py: 1.25,
              color: "#405261",
              "&:hover": { bgcolor: "#F4F6F8" },
            }}
          >
            <Box sx={{ mr: 1.25, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <IconChat24 />
            </Box>
            <ListItemText
              primary="Chat"
              primaryTypographyProps={{ fontSize: 16, color: "#405261" }}
            />
            {chatOpen ? (
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#8B97A2" }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18, color: "#8B97A2" }} />
            )}
          </ListItemButton>
          <Collapse in={chatOpen} unmountOnExit>
            <Stack direction="row" alignItems="stretch" sx={{ mt: 0.5 }} gap={0}>
              <Box
                aria-hidden
                sx={{
                  width: SUB_LIST_RAIL_GUTTER_PX,
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignSelf: "stretch",
                }}
              >
                <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#E5E9EC" }} />
              </Box>
              <List disablePadding sx={{ flex: 1, minWidth: 0, py: 0, overflow: "visible" }}>
                {CHAT_FILTERS.map((f) => {
                  const isActive = activeFilterId === f.id;
                  const count = filterCount(f.id, chatCounts);
                  return (
                    <ListItemButton
                      key={f.id}
                      selected={isActive}
                      onClick={() => onFilterSelect?.(f.id)}
                      sx={{
                        position: "relative",
                        borderRadius: "9px",
                        px: 1.25,
                        py: 1,
                        bgcolor: "transparent",
                        "&.Mui-selected": { bgcolor: "transparent" },
                        "&.Mui-selected:hover": { bgcolor: "transparent" },
                        "&:hover": { bgcolor: "transparent" },
                        ...(isActive && {
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: `${ACTIVE_RAIL_LEFT_PX}px`,
                            top: 0,
                            bottom: 0,
                            width: "1px",
                            bgcolor: "#148DFD",
                            zIndex: 1,
                            pointerEvents: "none",
                          },
                        }),
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                      >
                        <ListItemText
                          primary={f.label}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? "#061522" : "#405261",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? "#061522" : "#405261",
                          }}
                        >
                          {count}
                        </Typography>
                      </Stack>
                    </ListItemButton>
                  );
                })}
              </List>
            </Stack>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}

export default ChatNavSidebar;
